import { Alert, Button } from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeature } from '@/features/shared/hooks';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { discardCatalogAction, replayCatalogQueue } from './catalog-queue';
import { catalogStorageEstimate, syncCatalog } from './device-copy';
import { useCatalogSyncState, usePendingCatalog } from './hooks';
import { currentFormatLocale } from '@/lib/i18n';

type Estimate = Awaited<ReturnType<typeof catalogStorageEstimate>>;

const megabytes = (bytes: number | null) =>
  bytes === null ? '–' : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Profile section: when the catalog was last copied to this device, how much space it uses,
 * and what still waits for its sync (docs/finished/ingredient-catalog-spec.md 6.9).
 */
export function CatalogSyncSection() {
  const { t } = useTranslation();
  const enabled = useFeature('ingredient_catalog');
  const online = useOnlineStatus();
  const state = useCatalogSyncState();
  const pending = usePendingCatalog();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (enabled) void catalogStorageEstimate().then(setEstimate);
  }, [enabled, state?.syncedAt, pending.length]);

  if (!enabled) return null;
  const syncedAt = state?.syncedAt
    ? new Intl.DateTimeFormat(currentFormatLocale(), {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(state.syncedAt))
    : null;

  const run = async () => {
    setSyncing(true);
    try {
      await replayCatalogQueue();
      await syncCatalog();
    } finally {
      setSyncing(false);
    }
  };

  return (
    <section
      className="space-y-3"
      aria-labelledby="catalog-sync-heading"
      data-testid="catalog-sync"
    >
      <h2 id="catalog-sync-heading" className="text-lg font-semibold">
        {t('catalog.sync.title')}
      </h2>
      <p className="text-sm text-muted-foreground">{t('catalog.sync.body')}</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted-foreground">{t('catalog.sync.last')}</dt>
          <dd data-testid="catalog-sync-last">{syncedAt ?? t('catalog.sync.never')}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('catalog.sync.contents')}</dt>
          <dd>
            {t('catalog.sync.counts', {
              products: estimate?.products ?? 0,
              hints: estimate?.hints ?? 0,
              stores: estimate?.stores ?? 0,
            })}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('catalog.sync.storage')}</dt>
          <dd>
            {megabytes(estimate?.usageBytes ?? null)}
            {estimate?.quotaBytes ? ` / ${megabytes(estimate.quotaBytes)}` : ''}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">{t('catalog.sync.persisted')}</dt>
          <dd>
            {estimate?.persisted === null || estimate === null
              ? '–'
              : estimate.persisted
                ? t('common.yes')
                : t('common.no')}
          </dd>
        </div>
      </dl>
      {pending.length > 0 && (
        <Alert variant={pending.some((p) => p.lastError) ? 'warning' : 'info'}>
          <p>{t('catalog.pendingCount', { count: pending.length })}</p>
          <ul className="mt-1 space-y-1 text-xs">
            {pending
              .filter((p) => p.lastError)
              .map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2">
                  <span>
                    {t(`catalog.sync.kind.${p.kind}`)}: {p.lastError}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() => p.id !== undefined && void discardCatalogAction(p.id)}
                  >
                    {t('catalog.sync.discard')}
                  </Button>
                </li>
              ))}
          </ul>
        </Alert>
      )}
      <Button variant="outline" onClick={() => void run()} loading={syncing} disabled={!online}>
        {t('catalog.sync.now')}
      </Button>
    </section>
  );
}
